import { useCallback, useEffect, useState } from 'react'
import {
  api, ApiError, prettyPhone,
  type CallDay, type CallRecord,
} from '../lib/api'
import { SaveNumber } from './SaveNumber'

/** The call list — section 12's "the day's activity".
 *
 *  THE QUESTION THIS SCREEN ANSWERS IS "DID I MISS ANYBODY".
 *
 *  Not "show me a table of calls". A dispatcher opens this coming on shift, or
 *  coming off a long call, and needs the answer before reading anything. So
 *  the missed ones are counted at the top and can be shown alone with one
 *  press, and everything else is secondary.
 *
 *  AN OUTCOME WE DO NOT KNOW IS SHOWN AS UNKNOWN.
 *
 *  Where the PBX told us nothing either way the row says so, rather than
 *  guessing. A call wrongly shown as missed sends somebody ringing back a
 *  caller who has already been spoken to; a row that admits it does not know
 *  costs one glance.
 */

function hhmm(iso: string): string {
  return new Date(iso).toLocaleTimeString([], {
    hour: '2-digit', minute: '2-digit', hour12: false,
  })
}

function secs(n: number | null): string {
  if (n === null || n === undefined) return ''
  if (n < 60) return `${n}s`
  const m = Math.floor(n / 60)
  const s = n % 60
  return s ? `${m}m ${s}s` : `${m}m`
}

const LABEL: Record<string, string> = {
  answered: 'answered',
  missed: 'missed',
  busy: 'busy',
  failed: 'failed',
  voicemail: 'voicemail',
}

function today(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function Calls({ onTakeCall }: {
  /** Start an incident from a call, with the caller already filled in. The
   *  point of a missed-call list is that something can be done about one. */
  onTakeCall?: (caller: { caller_name: string; caller_phone: string }) => void
}) {
  const [data, setData] = useState<CallDay | null>(null)
  const [day, setDay] = useState<string>(today())
  const [missedOnly, setMissedOnly] = useState(false)
  const [q, setQ] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setData(await api.calls({ day, missedOnly, q: q.trim() || undefined }))
      setError(null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load the calls.')
    }
  }, [day, missedOnly, q])

  useEffect(() => {
    const t = setTimeout(load, 200)
    return () => clearTimeout(t)
  }, [load])

  // The phone is live: a call that comes in while this is open should appear.
  useEffect(() => {
    const t = setInterval(load, 20_000)
    return () => clearInterval(t)
  }, [load])

  async function handled(call: CallRecord, on: boolean) {
    setBusy(call.id)
    try {
      await api.callHandled(call.id, on)
      await load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not update that call.')
    } finally {
      setBusy(null)
    }
  }

  // Which row is having a caller added.
  //
  // This was a browser prompt: it took a name and threw away everything else
  // the dispatcher had just been told — the second number, the language they
  // speak, what the next person should know before answering. That is the one
  // moment the organization learns those things, and it was the moment they
  // were being discarded. The phone book's own fields open instead.
  const [addingTo, setAddingTo] = useState<CallRecord | null>(null)

  const t = data?.totals

  return (
    <div className="workspace">
      <div className="card">
        <h2>
          Calls
          {t !== undefined && (
            <span className={`pill ${t.missed ? 'error' : 'live'}`}>
              {t.missed ? `${t.missed} missed` : 'none missed'}
            </span>
          )}
        </h2>
        <p className="blurb">
          Every call that reached this organization, whether or not anybody
          picked it up and whether or not it became an incident. Times are shown
          in {data?.timezone ?? 'the organization timezone'}.
        </p>

        {error && <div className="notice">{error}</div>}

        {/* An empty list is the same picture whether the shift was quiet or
            the phone link has been down for hours. Saying which is the whole
            difference between a reassuring screen and a truthful one. */}
        {data && !data.listening && (
          <div className="notice">
            <strong>Not connected to the phone system.</strong> Calls arriving
            now are not being recorded, so this list is incomplete for as long
            as that lasts — an empty list here does not mean a quiet shift.
          </div>
        )}

        <div className="callbar">
          <input
            type="date"
            value={day}
            max={today()}
            onChange={(e) => setDay(e.target.value || today())}
          />
          <button
            className={`chip ${missedOnly ? 'on' : ''}`}
            onClick={() => setMissedOnly(!missedOnly)}
          >
            Missed only
          </button>
          <input
            className="grow"
            value={q}
            placeholder="Name or number"
            onChange={(e) => setQ(e.target.value)}
          />
          {day !== today() && (
            <button className="ghost tiny" onClick={() => setDay(today())}>
              Back to today
            </button>
          )}
        </div>

        {t && (
          <p className="connstatus">
            {t.calls} call{t.calls === 1 ? '' : 's'}
            {t.answered ? `, ${t.answered} answered` : ''}
            {t.missed ? `, ${t.missed} missed` : ''}
            {t.became_calls ? `, ${t.became_calls} became an incident` : ''}
            {t.strangers ? `, ${t.strangers} from a number not in the phone book` : ''}
            {t.unknown
              ? `. ${t.unknown} with no outcome recorded — the PBX did not say ` +
                'whether they were answered.'
              : ''}
          </p>
        )}

        {!data ? null : data.calls.length === 0 ? (
          <div className="listempty">
            {missedOnly
              ? 'Nothing missed. '
              : q
                ? 'No call matches that. '
                : 'No calls on this day. '}
            {day === today() && !q && !missedOnly &&
              'Calls appear here the moment the phone rings.'}
          </div>
        ) : (
          data.calls.map((c) => (
            <Row
              key={c.id}
              call={c}
              busy={busy === c.id}
              onHandled={handled}
              onAdd={setAddingTo}
              onTake={onTakeCall}
              adding={addingTo?.id === c.id}
              onCancelAdd={() => setAddingTo(null)}
              onSaved={async () => { setAddingTo(null); await load() }}
            />
          ))
        )}
      </div>
    </div>
  )
}

function Row({
  call, busy, onHandled, onAdd, onTake, adding, onCancelAdd, onSaved,
}: {
  call: CallRecord
  busy: boolean
  onHandled: (c: CallRecord, on: boolean) => void
  onAdd: (c: CallRecord) => void
  onTake?: (caller: { caller_name: string; caller_phone: string }) => void
  adding: boolean
  onCancelAdd: () => void
  onSaved: () => void
}) {
  const missed = call.disposition === 'missed'
  const unknown = call.disposition === null

  return (
    <>
    <div className={`listrow call ${missed && !call.handled_at ? 'missed' : ''}`}>
      <span className="mono when">{hhmm(call.started_at)}</span>

      <span className="nm who">
        {call.contact_name ?? prettyPhone(call.phone_e164) ?? 'Unknown'}
      </span>
      {call.contact_name && (
        <span className="mono dim">{prettyPhone(call.phone_e164)}</span>
      )}

      <span className={`calltag ${call.disposition ?? 'unknown'}`}>
        {unknown ? 'outcome not recorded' : LABEL[call.disposition!]}
      </span>

      {/* Ring time and talk time are different facts and never share a slot. */}
      <span className="dim timing">
        {call.duration_seconds !== null
          ? `${secs(call.duration_seconds)} on the call`
          : call.ring_seconds !== null
            ? `rang ${secs(call.ring_seconds)}`
            : ''}
      </span>

      {/* Section 12's "who took it". */}
      {call.extension && (
        <span className="dim">
          {call.disposition === 'answered' ? 'on' : 'rang'} ext {call.extension}
        </span>
      )}

      {call.incident_number && (
        <span className="tag">{call.incident_number}</span>
      )}

      <span className="spacer" />

      {!call.contact_id && call.phone_e164 && (
        <button className="ghost tiny" onClick={() => onAdd(call)}>
          Add to phone book
        </button>
      )}

      {missed && onTake && (
        <button
          className="ghost tiny"
          onClick={() =>
            onTake({
              caller_name: call.contact_name ?? '',
              caller_phone: call.phone_e164 ?? '',
            })
          }
        >
          Start a call
        </button>
      )}

      {missed && (
        <button
          className={`ghost tiny ${call.handled_at ? 'ok' : ''}`}
          disabled={busy}
          onClick={() => onHandled(call, !call.handled_at)}
          title={
            call.handled_at
              ? 'Marked as dealt with. Press to undo.'
              : 'I have rung them back or otherwise dealt with this.'
          }
        >
          {call.handled_at ? 'Dealt with' : 'Mark dealt with'}
        </button>
      )}
    </div>

    {/* The phone book's own form, in place, under the row it belongs to. */}
    {adding && call.phone_e164 && (
      <div className="detail">
        <SaveNumber
          phone={call.phone_e164}
          suggestedName={call.contact_name ?? ''}
          onCancel={onCancelAdd}
          onDone={onSaved}
        />
      </div>
    )}
    </>
  )
}
